import { catchError, exhaustMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';

import * as fromRoot from '../reducers';
import * as automation from '../actions/automation';

import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { AutomationSource } from '../discover/dependencies/automationsSource';
import {
  ActionType,
  DigitalMarketingAutomationSource
} from '../discover/dependencies/digital-marketing-automation-source';
import { AutomationTemplate } from '../models/discover/automation-template.model';
import {
  CampaignSentConfirmationModalComponent
} from '../shared/campaign-form/campaign-sent-confirmation-modal/campaign-sent-confirmation-modal.component';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { DiscoverConstants } from 'spotlight-ui/app/discover/discover.constants';
import { Automation } from 'spotlight-ui/app/models/discover/automation.model';
import { CampaignSource } from 'spotlight-ui/app/engage/dependencies/ICampaignSource';
import { DigitalMarketingCampaignSource } from 'spotlight-ui/app/engage/dependencies/DigitalMarketingCampaignSource';
import { DigitalMarketingAudienceSource } from 'spotlight-ui/app/meet/dependencies/digital-marketing-audience-source';
import { AudienceSource } from 'spotlight-ui/app/meet/dependencies/audience-source';
import moment from 'moment-timezone';
import { MeetConstants } from 'spotlight-ui/app/meet/meet.constants';
import {
  CampaignType,
  RewardDetails,
} from 'spotlight-ui/app/models/engage/campaign-details.model';
import { AnalyticsService } from '../core/analytics';

@Injectable()
export class AutomationsEffects {
  private campaignSentModal: MatDialogRef<CampaignSentConfirmationModalComponent, any>;
  constructor(
        private readonly actions$: Actions,
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly store: Store<fromRoot.State>,
        private readonly http: HttpClient,
        private readonly analytics: AnalyticsService,
  ) { }

  private readonly automationsSource: AutomationSource = new DigitalMarketingAutomationSource(this.http);
  private readonly campaignSource: CampaignSource = new DigitalMarketingCampaignSource(this.http);
  private readonly audienceSource: AudienceSource = new DigitalMarketingAudienceSource(this.http);

  public fetchAutomationTemplateDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(automation.ActionTypes.SELECT_AUTOMATION),
      filter((action: any) => { return action.payload.engagementMode === DiscoverConstants.create }),
      map((action: any) => action.payload),
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      switchMap(([payload, location]) =>
        this.automationsSource.getAutomationTemplate(
          location.locationNumber,
          DiscoverConstants.tileToAutomationIdsMapping[payload.id]
        ).pipe(
          map((updatedRes: AutomationTemplate) => {
            return new automation.SelectAutomationSuccess(updatedRes);
          }),
          catchError((error) => of(new automation.SelectAutomationFailure(error)))
        )
      )
    );
  });

  public fetchAutomationDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(automation.ActionTypes.SELECT_AUTOMATION),
      filter((action: any) => { return action.payload.engagementMode === DiscoverConstants.edit }),
      map((action: any) => action.payload),
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      switchMap(([payload, location]) =>
        this.automationsSource.getAutomationById(location.locationNumber, payload.fieldId).pipe(
          map((details: {automationDetails: Automation, template: AutomationTemplate}) => {
            let updatedRes = {
              ...details.template,
              ...details.automationDetails
            }
            return new automation.SelectAutomationSuccess(updatedRes);
          }),
          catchError((error) => of(new automation.SelectAutomationFailure(error)))
        )
      )
    );
  });

  // Fetch audience data when coming from external link (count is 0 and customers is empty)
  public fetchAutomationAudience$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(automation.ActionTypes.SELECT_AUTOMATION),
      filter((action: any) => {
        // Only fetch audience when count is 0 and customers is empty (external link case)
        const count = action.payload?.count ?? 0;
        const customers = action.payload?.customers ?? [];
        return count === 0 && customers.length === 0;
      }),
      map((action: any) => action.payload),
      concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
      switchMap(([payload, location]) => {
        const automationTemplateId = DiscoverConstants.tileToAutomationIdsMapping[payload.id];
        return this.automationsSource.getAutomationAudience(location.locationNumber, automationTemplateId).pipe(
          map((audienceData) => new automation.UpdateAutomationAudience({
            count: audienceData.count,
            customers: audienceData.customers
          })),
          catchError(() => of({ type: 'NO_OP' } as Action)) // Silently fail if audience fetch fails
        );
      })
    );
  });

  public createAutomation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(automation.ActionTypes.CREATE_AUTOMATION),
      exhaustMap(() => this.store.select(fromRoot.getSelectedAutomation).pipe(
        take(1),
        concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
        switchMap(([payload, location]) => {
          if (payload.automationDetails?.isAutomated) {
            return this.createAutomation(location, payload)
          } else {
            return this.createCampaign(location,payload);
          }
        })
      ))
    )
  );

  public updateAutomation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(automation.ActionTypes.UPDATE_AUTOMATION),
      exhaustMap(() => this.store.select(fromRoot.getSelectedAutomation).pipe(
        take(1),
        concatLatestFrom(() => this.store.select(fromRoot.getSelectedLocation)),
        switchMap(([payload, location]) => {
          this.analytics.sendEvent('Automation Edited', this.buildAutomationAnalyticsPayload(payload));
          return this.automationsSource.updateAutomation(location.locationNumber, payload.automationID, payload).pipe(
            map(automationID => new automation.UpdateAutomationComplete({ automationID, type: ActionType.Edit } )),
            catchError((error) => of(new automation.UpdateAutomationComplete( { error, type: ActionType.Edit } )))
          );
        })
      ))
    )
  );

  public stopAutomation$ = createEffect(() =>this.actions$.pipe(
    ofType(automation.ActionTypes.STOP_AUTOMATION),
    tap((action: any) => this.analytics.sendEvent('Automation Stopped', action.payload)),
    switchMap((action: any) =>
      forkJoin([
        this.store.select(fromRoot.getSelectedLocation).pipe(take(1)),
        this.store.select(fromRoot.getDiscoverTile(action.payload.id)).pipe(take(1))
      ]).pipe(
        switchMap(([location, discoverTile]: any[]) =>
          this.automationsSource.stopAutomation(location.locationNumber, discoverTile.id, discoverTile.fieldId).pipe(
            map(() =>{
              return new automation.StopAutomationComplete({ ...discoverTile, type: ActionType.Stop }, location.locationNumber)
            }),
            catchError((error) =>
              of(new automation.StopAutomationComplete({ error, type: ActionType.Stop, ...discoverTile }, location.locationNumber))
            )
          )
        )
      )
    ))
  );

  public createdAutomationFlowStart$ = createEffect(() => { return this.actions$.pipe(
    ofType(
      automation.ActionTypes.CREATE_AUTOMATION,
      automation.ActionTypes.UPDATE_AUTOMATION,
      automation.ActionTypes.STOP_AUTOMATION,
    ),
    concatLatestFrom(() => this.store.select(fromRoot.getIsMobile)),
    tap(([, isMobile]) => this.openCampaignSendConfirmationDialog(isMobile))
  )},{ dispatch: false });

  public createdAutomationFlowComplete$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      automation.ActionTypes.CREATE_AUTOMATION_COMPLETE,
      automation.ActionTypes.UPDATE_AUTOMATION_COMPLETE,
      automation.ActionTypes.STOP_AUTOMATION_COMPLETE
    ),
    switchMap((action: any) => this.store.select(
      fromRoot.getAutomationConfirmation(action.payload?.type, action.payload?.campaignType ?? CampaignType.Automation)).pipe(take(1))
    ),
    tap((campaign) => this.updateCampaignSendConfirmationDialog(campaign))
  )},{ dispatch: false });

  /** Discover tile id for the selected automation, matching the `Flow ID` the one-page flow reports. */
  private getFlowId(payload): string {
    return DiscoverConstants.automationToTileIdsMapping[payload?.templateID] ?? payload?.templateID;
  }

  private buildAutomationAnalyticsPayload(payload) {
    return {
      'Flow ID': this.getFlowId(payload),
      'Include Active Treats': payload?.automationCampaignDetails?.includeActiveLocationRewardCustomers,
      'Treat Validity Length': payload?.automationCampaignDetails?.durationDays,
    };
  }

  private createCampaign(location, payload) {
    // One-time send from an automation tile; mirrors the one-page flow's 'Discover Flow Sent'
    this.analytics.sendEvent('Discover Flow Sent', { flowId: this.getFlowId(payload) });
    let individualCustomers ={ selectedFilters: [{ id: MeetConstants.includeFilterId, cfaIdList: payload.customerIDs }] }

    return this.store.select(fromRoot.getFeatureEnabled(DiscoverConstants.adhocCuratedCampaignHoldoutsFlag)).pipe(
      take(1),
      switchMap((holdoutsEnabled) => {
        const campaignInputs = this.mapAutomationPayloadToCampaign(payload, holdoutsEnabled);
        return this.audienceSource.createAudience(
          location.locationNumber,
          individualCustomers,
          true,
          campaignInputs.sendingOptions.sendDate
        ).pipe(
          switchMap((audienceID) => {
            return this.campaignSource.createCampaign(location.locationNumber, { ...campaignInputs, audienceID }).pipe(
              map((campaignID: string) => new automation.CreateAutomationComplete({
                automated: false,
                id: '',
                fieldId: campaignID,
                automationID: campaignID,
                campaignType: payload.type,
                type: ActionType.Create
              })),
            );
          }),
          catchError(error => of (new automation.CreateAutomationComplete({ error,type: ActionType.Create })))
        );
      })
    );
  }

  private mapAutomationPayloadToCampaign(payload, holdoutsEnabled: boolean) {
    let template = payload.automationCampaignDetails.campaignTemplates.find(campaign=>
      campaign.id === payload.automationCampaignDetails.selectedCampaignTemplateID);
    let start = moment().add(15, 'seconds');
    let end = moment().add(payload.automationCampaignDetails.durationDays, 'days');
    if (end.day() === 0 ) {
      end = end.add(1, 'day');
    }
    return {
      customizations: payload.customizations,
      goal: holdoutsEnabled && payload.goal ? payload.goal : undefined,
      rewardInformation: {
        startDatetime: start,
        endDatetime: end,
        appMessage:payload.automationCampaignDetails.appMessage,
        includeOperatorImage: false,
        rewardID: template.rewardId,
        name: payload.automationCampaignDetails.title,
        includeActiveLocationRewardCustomers:payload.automationCampaignDetails.includeActiveLocationRewardCustomers
      } as RewardDetails,
      template: template,
      sendingOptions:   {
        sendImmediate: true,
        sendDate: start,
        sendTime: start.format('hh:mm a'),
        groupNow: true,
      },
      cfaIDs: payload.customerIDs,
      notificationID: template?.notificationLayouts[0].id
    }
  }

  private createAutomation(location, payload) {
    this.analytics.sendEvent('Automation Flow Sent', this.buildAutomationAnalyticsPayload(payload));
    return this.automationsSource.createAutomation(location.locationNumber, payload).pipe(
      map(automationID => new automation.CreateAutomationComplete(
        {
          automated: true,
          id: DiscoverConstants.automationToTileIdsMapping[payload.templateID],
          fieldId: automationID,
          automationID: automationID,
          campaignType: CampaignType.Automation,
          type: ActionType.Create
        })),
      catchError((error) => of(new automation.CreateAutomationComplete({ error, type: ActionType.Create } )))
    )
  }

  private openCampaignSendConfirmationDialog(isMobile: boolean) {
    let config: MatDialogConfig = {
      autoFocus: false,
      disableClose: true,
      panelClass:'dialog-responsive',
      data: { loading: true, isMobile }
    }
    this.campaignSentModal = this.dialog.open(CampaignSentConfirmationModalComponent, config);
  }

  private updateCampaignSendConfirmationDialog(campaign) {
    if (this.campaignSentModal?.getState() === MatDialogState.OPEN) {
      this.campaignSentModal.componentInstance.campaign = {
        ...this.campaignSentModal.componentInstance.campaign,
        ...campaign,
        loading: false
      };
    }

  }

}
